import React, { useMemo, useState } from 'react';
import { Sidebar } from './components/Sidebar';
import { KPICards } from './components/KPICards';
import { CampaignOverviewChart } from './components/CampaignOverviewChart';
import { CampaignGrid } from './components/CampaignGrid';
import { allCampaigns } from './data/mockData';
export type ChartMetric = 'budget' | 'leads' | 'cpc';
const formatCurrency = (value: number) => {
  if (value >= 1000000) return `€${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `€${(value / 1000).toFixed(0)}K`;
  return `€${value.toFixed(2)}`;
};
const formatNumber = (value: number) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toString();
};
export function App() {
  // Lifted Filter State
  const [activeBrand, setActiveBrand] = useState<string>('Toyota');
  const [activeModel, setActiveModel] = useState<string>('Yaris Cross');
  const [activeType, setActiveType] = useState<string | null>('Tactical');
  const [activeYears, setActiveYears] = useState<string[]>([]);
  const [activeMarkets, setActiveMarkets] = useState<string[]>([]);
  const [chartMetric, setChartMetric] = useState<ChartMetric>('budget');
  // Derived State: Filtered Campaigns
  const filteredCampaigns = useMemo(() => {
    return allCampaigns.filter(campaign => {
      if (campaign.brand !== activeBrand) return false;
      if (campaign.model !== activeModel) return false;
      if (activeType && campaign.type !== activeType) return false;
      if (activeYears.length > 0 && !activeYears.includes(campaign.year)) return false;
      if (activeMarkets.length > 0 && !activeMarkets.includes(campaign.market)) return false;
      return true;
    });
  }, [activeBrand, activeModel, activeType, activeYears, activeMarkets]);
  // Derived State: KPI Data
  const kpiData = useMemo(() => {
    let totalBudget = 0;
    let totalLeads = 0;
    filteredCampaigns.forEach(c => {
      totalBudget += c.metrics.rawBudget;
      totalLeads += c.metrics.rawLeads;
    });
    const costPerLead = totalLeads > 0 ? totalBudget / totalLeads : 0;
    return {
      totalCampaigns: filteredCampaigns.length,
      budget: formatCurrency(totalBudget),
      leads: formatNumber(totalLeads),
      costPerLead: formatCurrency(costPerLead)
    };
  }, [filteredCampaigns]);
  // Helper to get metric value from a campaign
  const getMetricValue = (campaign: (typeof filteredCampaigns)[0]) => {
    switch (chartMetric) {
      case 'budget':
        return campaign.metrics.rawBudget;
      case 'leads':
        return campaign.metrics.rawLeads;
      case 'cpc':
        return campaign.metrics.rawClicks > 0 ? Math.round(campaign.metrics.rawBudget / campaign.metrics.rawClicks * 100) / 100 : 0;
    }
  };
  // Derived State: Chart Data — campaigns grouped by year, each campaign its own bar
  const chartData = useMemo(() => {
    const years = ['2023', '2024', '2025', '2026'];
    const sorted = [...filteredCampaigns].sort((a, b) => {
      if (a.year !== b.year) return a.year.localeCompare(b.year);
      return a.date.localeCompare(b.date);
    });
    const brandColors = ['#1e293b', '#334155', '#475569', '#0f172a'];
    const tacticalColors = ['#ef4444', '#f97316', '#ec4899', '#e11d48'];
    const yearTypeCounters: Record<string, number> = {};
    const datasets = sorted.map(campaign => {
      const yearIndex = years.indexOf(campaign.year);
      const value = getMetricValue(campaign);
      const data = years.map((_, i) => i === yearIndex ? value : null);
      const key = `${campaign.year}-${campaign.type}`;
      const idx = yearTypeCounters[key] || 0;
      yearTypeCounters[key] = idx + 1;
      const palette = campaign.type === 'Brand' ? brandColors : tacticalColors;
      const color = palette[idx % palette.length];
      return {
        label: campaign.title,
        data,
        backgroundColor: color,
        borderRadius: 6,
        barPercentage: 0.8,
        categoryPercentage: 0.85
      };
    });
    return {
      labels: years,
      datasets
    };
  }, [filteredCampaigns, chartMetric]);
  return <div className="min-h-screen bg-slate-50 text-foreground font-sans" data-id="element-0">
      <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8" data-id="element-1">
        {/* Dashboard Header */}
        <div className="mb-6 lg:mb-8" data-id="element-2">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight" data-id="element-3">
            Campaign Analytics
          </h1>
          <p className="text-sm md:text-base text-muted-foreground mt-1" data-id="element-4">
            Marketing Dashboard
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8" data-id="element-5">
          <Sidebar activeBrand={activeBrand} setActiveBrand={setActiveBrand} activeModel={activeModel} setActiveModel={setActiveModel} activeType={activeType} setActiveType={setActiveType} activeYears={activeYears} setActiveYears={setActiveYears} activeMarkets={activeMarkets} setActiveMarkets={setActiveMarkets} data-id="element-6" />

          <main className="flex-1 flex flex-col gap-6 lg:gap-8 min-w-0" data-id="element-7">
            <KPICards data={kpiData} data-id="element-8" />
            <CampaignOverviewChart data={chartData} chartMetric={chartMetric} setChartMetric={setChartMetric} data-id="element-9" />
            <CampaignGrid campaigns={filteredCampaigns} data-id="element-10" />
          </main>
        </div>
      </div>
    </div>;
}